import React, {useCallback, useState} from 'react';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import {useIntl} from 'react-intl';

import Loader from '../dashboard/Loader';
import ErrorModal from '../ui/ErrorModal';
import API from '../../common/utils/API';
import {DateTimeCell, VenueLocalDateTimeCell} from './formatDateTime';
import {BandBadge} from './BandBadge';
import PromotedFilterBar from './PromotedFilterBar';
import {usePromotedFetch} from './usePromotedFetch';
import {TABLE_SCROLL_HEIGHT, YesNo, emptyMessageId} from './promotedShared';

const fetchManual = (filters) =>
    API.reports.getRelevancePromotedManual(filters);

/**
 * Fila memoizada: un cambio de `loading` no vuelve a pintar las filas cuyo
 * evento no cambió (rerender-memo). `onSelectEvent` llega estable desde
 * `RelevancePage` (setter de useState).
 */
const ManualRow = React.memo(function ManualRow({event, onSelectEvent}) {
    const intl = useIntl();
    return (
        <tr
            data-test='promotedManualRow'
            onClick={() => onSelectEvent(event.eventId)}
            style={{cursor: 'pointer'}}
        >
            <td>{event.eventId}</td>
            <td>{event.name}</td>
            <td>
                <VenueLocalDateTimeCell iso={event.startsAt} />
            </td>
            <td>{event.priorityOrder}</td>
            <td>
                <DateTimeCell iso={event.pinnedUntil} />
            </td>
            <td data-test='promotedManualBand'>
                <BandBadge intl={intl} band={event.band} />
            </td>
            <td data-test='promotedManualAutoRanking'>
                <YesNo value={event.autoRankingEnabled} />
            </td>
            <td data-test='promotedManualDemandBanner'>
                <YesNo value={event.demandBannerEnabled} />
            </td>
        </tr>
    );
});

/**
 * Tabla "Manuales": eventos de la categoría destino, sin paginar, con su
 * propia barra de filtros. Avisa `applyEnabled`/`featuredCategoryId` al
 * contenedor con `onMeta` para el aviso de motor apagado.
 */
function ManualPromotedTable({onSelectEvent, onMeta}) {
    const intl = useIntl();
    const t = (id) => intl.formatMessage({id});
    const [filters, setFilters] = useState({});
    // El aviso de motor apagado se resuelve al llegar la respuesta, no con un
    // efecto sobre `data` (rerender-move-effect-to-event).
    const {loading, error, data, refresh, dismissError} = usePromotedFetch(
        fetchManual,
        filters,
        (response) =>
            onMeta &&
            onMeta({
                applyEnabled: response.applyEnabled,
                featuredCategoryId: response.featuredCategoryId,
            }),
    );
    const items = (data && data.items) || [];
    const filtersActive = Object.keys(filters).length > 0;
    const clearFilters = useCallback(() => setFilters({}), []);

    return (
        <Card className='mb-4' data-test='promotedManualCard'>
            <Card.Header className='d-flex justify-content-between align-items-center'>
                {t('RELEVANCE.PROMOTED_MANUAL_HEADER')}
                <Button
                    variant='secondary'
                    size='sm'
                    onClick={refresh}
                    data-test='promotedManualRefresh'
                >
                    {t('RELEVANCE.REFRESH_BUTTON')}
                </Button>
            </Card.Header>
            <Card.Body style={{position: 'relative'}}>
                <Loader loading={loading} />
                <PromotedFilterBar
                    testIdPrefix='promotedManual'
                    label={t('RELEVANCE.PROMOTED_FILTER_MANUAL_LABEL')}
                    active={filtersActive}
                    onApply={setFilters}
                    onClear={clearFilters}
                />
                {items.length === 0 && !loading ? (
                    <p data-test='promotedManualEmpty'>
                        {t(emptyMessageId(filtersActive))}
                    </p>
                ) : (
                    <div
                        data-test='promotedManualTableScroll'
                        style={{
                            maxHeight: TABLE_SCROLL_HEIGHT,
                            overflowY: 'auto',
                        }}
                    >
                        <Table
                            striped
                            bordered
                            hover
                            size='sm'
                            data-test='promotedManualTable'
                        >
                            <thead>
                                <tr>
                                    <th>{t('RELEVANCE.PROMOTED_TABLE_ID')}</th>
                                    <th>
                                        {t('RELEVANCE.PROMOTED_TABLE_NAME')}
                                    </th>
                                    <th>
                                        {t(
                                            'RELEVANCE.PROMOTED_TABLE_STARTS_AT',
                                        )}
                                    </th>
                                    <th>
                                        {t('RELEVANCE.PROMOTED_TABLE_PRIORITY')}
                                    </th>
                                    <th>
                                        {t(
                                            'RELEVANCE.PROMOTED_TABLE_PINNED_UNTIL',
                                        )}
                                    </th>
                                    <th>
                                        {t('RELEVANCE.PROMOTED_TABLE_BAND')}
                                    </th>
                                    <th>
                                        {t(
                                            'RELEVANCE.PROMOTED_TABLE_AUTO_RANKING',
                                        )}
                                    </th>
                                    <th>
                                        {t(
                                            'RELEVANCE.PROMOTED_TABLE_DEMAND_BANNER',
                                        )}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((event) => (
                                    <ManualRow
                                        key={event.eventId}
                                        event={event}
                                        onSelectEvent={onSelectEvent}
                                    />
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Card.Body>
            <ErrorModal
                isLoadErrorModalOpen={error}
                toggleLoadErrorModal={dismissError}
                errorMessage={t('RELEVANCE.LOAD_ERROR')}
                handleRetryClick={refresh}
                returnUrl='/relevance'
                loading={loading}
            />
        </Card>
    );
}

export default ManualPromotedTable;
